import { motion } from "framer-motion";
import { Megaphone, Users, BrainCircuit, BadgeCheck, MapPin, ChevronRight } from "lucide-react";

const pipeline = [
  { icon: Megaphone, label: "You report", desc: "Post a photo, video or tip from your locality" },
  { icon: Users, label: "Community backs", desc: "Nearby readers upvote and add context" },
  { icon: BrainCircuit, label: "AI screens", desc: "Forensics check media for manipulation" },
  { icon: BadgeCheck, label: "Editor verifies", desc: "Human editors publish with a verified badge" },
];

const reports = [
  { title: "Waterlogging at MG Road underpass after 2 hrs of rain", place: "Bengaluru", status: "Published", statusClass: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300", upvotes: 1284 },
  { title: "Govt school in Ward 12 gets smart classrooms this week", place: "Patna", status: "Under Review", statusClass: "border-amber-500/30 bg-amber-500/10 text-amber-300", upvotes: 412 },
  { title: "Broken traffic signal at City Circle fixed within 48 hrs of report", place: "Jaipur", status: "Verified", statusClass: "border-cyan-500/30 bg-cyan-500/10 text-cyan-300", upvotes: 876 },
];

const PublicReporting = () => (
  <section id="community" data-testid="community-section" className="py-20 lg:py-32 border-t border-[#1E293B] bg-[#0B111C]/40">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl">
        <p className="mono-label">Public Reporting</p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-4">
          The newsroom is wherever you are.
        </h2>
        <p className="text-slate-400 mt-4 leading-relaxed">
          Citizen journalism with real accountability. Every public report travels through a
          transparent verification pipeline before it reaches the feed.
        </p>
      </div>

      <div className="mt-14 grid md:grid-cols-4 gap-4">
        {pipeline.map((step, i) => (
          <motion.div
            key={step.label}
            data-testid={`pipeline-step-${i}`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
            className="relative rounded-2xl border border-[#1E293B] bg-gradient-to-b from-slate-800/60 to-slate-900/80 p-6"
          >
            <span className="absolute top-5 right-5 text-[10px] font-mono-x text-slate-600">0{i + 1}</span>
            <span className="h-10 w-10 rounded-lg border border-cyan-500/25 bg-cyan-500/10 flex items-center justify-center">
              <step.icon className="h-5 w-5 text-cyan-400" />
            </span>
            <h3 className="font-display font-semibold mt-4">{step.label}</h3>
            <p className="text-xs text-slate-400 leading-relaxed mt-1.5">{step.desc}</p>
            {i < pipeline.length - 1 && (
              <ChevronRight className="hidden md:block absolute -right-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-600 z-10" />
            )}
          </motion.div>
        ))}
      </div>

      <div className="mt-10 rounded-2xl border border-[#1E293B] bg-slate-900/60 p-6 sm:p-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-display text-lg font-semibold">Live from the community</h3>
          <span className="inline-flex items-center gap-2 text-[10px] font-mono-x uppercase tracking-widest text-slate-500">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Sample feed
          </span>
        </div>
        <div className="mt-6 grid md:grid-cols-3 gap-4">
          {reports.map((r, i) => (
            <motion.div
              key={r.title}
              data-testid={`community-report-${i}`}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.08 }}
              className="glow-card rounded-xl border border-[#1E293B] bg-slate-900/80 p-5"
            >
              <div className="flex items-center justify-between gap-2">
                <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-mono-x uppercase tracking-widest ${r.statusClass}`}>
                  {r.status}
                </span>
                <span className="inline-flex items-center gap-1 text-[10px] font-mono-x text-slate-500">
                  <MapPin className="h-3 w-3" />
                  {r.place}
                </span>
              </div>
              <p className="text-sm text-slate-200 leading-snug mt-3 font-medium">{r.title}</p>
              <p className="mt-3 text-[10px] font-mono-x uppercase tracking-widest text-slate-500">
                {r.upvotes.toLocaleString()} community upvotes
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default PublicReporting;
